import { useState } from "react";
import { Alert, Button, Code, Skeleton, cn } from "@one-nexty/components-web";

import { CodePane, Preview } from "../ui/preview";
import { Section, SubSection } from "../ui/section";
import { durations, easings, leaf, parseCubicBezier } from "../lib/tokens";

/**
 * Les classes sont écrites EN ENTIER et non composées à l'exécution
 * (`animate-${name}`) : Tailwind analyse le texte source, une classe assemblée
 * dynamiquement n'existe nulle part dans le fichier et n'est donc jamais générée.
 * C'est le piège le plus fréquent avec Tailwind, et il est silencieux.
 */
const ANIMATIONS = [
  { className: "animate-fade-in", label: "fade-in", usage: "Apparition neutre" },
  { className: "animate-scale-in", label: "scale-in", usage: "Modale, popover" },
  { className: "animate-slide-up", label: "slide-up", usage: "Alerte, notification" },
  { className: "animate-slide-down", label: "slide-down", usage: "Accordéon, menu" },
  { className: "animate-pop", label: "pop", usage: "Bascule d'état binaire" },
] as const;

export function MotionSection() {
  // Changer la clé remonte les éléments : c'est le seul moyen de rejouer une
  // animation CSS, qui ne se déclenche qu'à l'entrée dans le DOM.
  const [replayKey, setReplayKey] = useState(0);

  return (
    <Section
      id="mouvement"
      eyebrow="Fondations"
      title="Mouvement"
      description="Cinq durées, quatre courbes, dérivées de tokens comme le reste. Toutes les animations du système ne portent que sur transform, opacity ou filter — les trois propriétés que le compositeur anime sans repasser par la mise en page."
    >
      <SubSection title="Durées" hint="duration-fast, duration-normal, duration-slow…">
        <div className="divide-y divide-border rounded-xl border border-border">
          {durations.map((token) => (
            <div key={token.name} className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3">
              <span className="w-40 shrink-0 whitespace-nowrap">
                <Code>duration-{leaf(token)}</Code>
              </span>
              <span className="w-14 shrink-0 font-mono text-[11px] text-fg-muted tabular-nums">
                {token.value}
              </span>

              {/* La bille parcourt la piste en boucle : on compare les durées
                  entre elles bien mieux qu'avec des nombres. */}
              <span className="relative h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-surface-sunken">
                <span
                  className="absolute inset-y-0 left-0 w-6 rounded-full bg-accent"
                  style={{
                    animation: `nx-indeterminate ${token.value} var(--ease-standard) infinite alternate`,
                  }}
                />
              </span>

              <p className="min-w-0 flex-1 text-xs text-fg-muted">{token.description}</p>
            </div>
          ))}
        </div>
      </SubSection>

      <SubSection title="Courbes" hint="ease-standard, ease-entrance, ease-spring…">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {easings.map((token) => (
            <EasingCard key={token.name} name={leaf(token)} value={token.value} description={token.description} />
          ))}
        </div>
      </SubSection>

      <SubSection title="Animations nommées" hint="Toutes en transform + opacity">
        <Preview
          label="animations.tsx"
          code={`<div className="animate-fade-in">…</div>
<div className="animate-scale-in">…</div>
<div className="animate-slide-up">…</div>
<div className="animate-pop">…</div>`}
        >
          <Button variant="outline" size="sm" onClick={() => setReplayKey((k) => k + 1)}>
            Rejouer
          </Button>
          {ANIMATIONS.map((animation) => (
            <span
              key={`${animation.label}-${replayKey}`}
              className={cn(
                animation.className,
                "inline-flex flex-col items-center gap-1 rounded-lg border border-border bg-surface-sunken px-4 py-3",
              )}
            >
              <span className="font-mono text-xs text-fg">{animation.className}</span>
              <span className="text-[11px] text-fg-muted">{animation.usage}</span>
            </span>
          ))}
        </Preview>
      </SubSection>

      <SubSection
        title="Balayage de chargement"
        hint="La bande est translatée sur le GPU, le bloc porteur n'est jamais repeint"
      >
        <Preview
          layout="stack"
          className="max-w-md"
          label="skeleton.tsx"
          code={`<Skeleton className="w-1/3" />
<SkeletonText lines={3} />`}
        >
          <Skeleton className="w-1/3" />
          <Skeleton className="w-full" />
          <Skeleton className="w-2/3" />
        </Preview>
      </SubSection>

      <SubSection title="Mouvement réduit">
        <Alert tone="info" title="prefers-reduced-motion est respecté par défaut">
          Le thème neutralise toutes les transitions quand le système le demande, à une exception
          près : ce qui porte <Code>data-motion-essential</Code>. Un indicateur de chargement figé
          ne signale plus rien.
        </Alert>
        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-surface-raised">
          <CodePane
            label="theme.css"
            code={`@media (prefers-reduced-motion: reduce) {
  *:not([data-motion-essential]),
  *:not([data-motion-essential])::before,
  *:not([data-motion-essential])::after,
  *:not([data-motion-essential])::backdrop {
    animation-duration: 1ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 1ms !important;
  }
}`}
          />
        </div>
      </SubSection>
    </Section>
  );
}

/**
 * Tracé d'une courbe de Bézier cubique.
 *
 * Le repère SVG a son origine en haut à gauche alors qu'une courbe d'easing se
 * lit de bas en haut : d'où les `100 - y` sur chaque ordonnée.
 */
function EasingCard({
  name,
  value,
  description,
}: {
  name: string;
  value: string;
  description?: string;
}) {
  const points = parseCubicBezier(value);

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-surface-raised p-4">
      <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-surface-sunken">
        {points && (
          <svg viewBox="-10 -10 120 120" className="size-full" aria-hidden="true">
            {/* Diagonale de référence : la courbe linéaire. */}
            <line x1="0" y1="100" x2="100" y2="0" className="stroke-border" strokeDasharray="4 4" />

            {/* Poignées de contrôle. */}
            <line
              x1="0"
              y1="100"
              x2={points[0] * 100}
              y2={100 - points[1] * 100}
              className="stroke-fg-muted/40"
            />
            <line
              x1="100"
              y1="0"
              x2={points[2] * 100}
              y2={100 - points[3] * 100}
              className="stroke-fg-muted/40"
            />

            <path
              d={`M 0 100 C ${points[0] * 100} ${100 - points[1] * 100}, ${points[2] * 100} ${100 - points[3] * 100}, 100 0`}
              fill="none"
              strokeWidth="3"
              strokeLinecap="round"
              className="stroke-accent"
            />

            <circle cx={points[0] * 100} cy={100 - points[1] * 100} r="4" className="fill-accent" />
            <circle cx={points[2] * 100} cy={100 - points[3] * 100} r="4" className="fill-accent" />
          </svg>
        )}

        {/*
          Bille animée avec la courbe correspondante : la lecture du tracé et la
          sensation réelle se recoupent immédiatement.

          L'élément animé occupe toute la largeur de la piste et porte la bille à
          son bord gauche. C'est ce qui permet d'exprimer le trajet en
          `translate3d(100% …)` — donc en transformation composée — au lieu d'un
          `left` qui remettrait la vignette en page à chaque image.
        */}
        <span aria-hidden="true" className="absolute inset-x-2 bottom-2 h-2.5">
          <span
            className="block h-full w-full transform-gpu will-change-transform"
            style={{ animation: `nx-demo-travel 1.6s ${value} infinite alternate` }}
          >
            <span className="block size-2.5 rounded-full bg-accent" />
          </span>
        </span>
      </div>

      <p className="font-mono text-xs text-fg">ease-{name}</p>
      <p className="text-[11px] leading-relaxed text-fg-muted">{description}</p>
      <p className="font-mono text-[10px] break-all text-fg-muted">{value}</p>
    </div>
  );
}
